import { queryOptions, useQuery } from '@tanstack/react-query';
import { SortDTO } from '../common';
import {
  DetectionsDictDTO,
  HeapRecordingControllerGetRecordingsData,
  HeapRecordingDTO,
  HeapRecordingFilterDTO,
  HeapRecordingService,
} from '../generated';
import { HeapDetection } from '../../components/Heap/types';

export const heapRecordingKeys = {
  base: () => ['heapRecording'] as const,
  list: ({ jobsiteId, filter, pageSize, pageNumber, sorting }: HeapRecordingListOptionsProps) =>
    [...heapRecordingKeys.base(), jobsiteId, filter, pageSize, pageNumber, sorting] as const,
};

export interface HeapRecording {
  id: string;
  recordedAt: Date;
  licensePlate?: string;
  hasDetections: boolean;
  detections: HeapDetection[];
  version?: string;
  jobsiteId: string;
  archived?: boolean;
  extraFields?: Record<string, unknown>;
  cameraId?: string;
  masks?: HeapRecordingDTO['masks'];
}

export interface HeapRecordingBatch {
  id: string;
  recordedAt: Date;
  licensePlate?: string;
  hasDetections: boolean;
  archived: boolean;
  jobsiteId: string;
  version?: string;
  cameras: HeapRecording[];
}

export interface HeapRecordingListOptionsProps {
  jobsiteId: string | undefined;
  filter: HeapRecordingFilterDTO;
  pageSize: number;
  pageNumber: number;
  sorting: SortDTO<HeapRecordingBatch>;
}

export function heapRecordingListOptions({
  jobsiteId = '',
  filter,
  pageSize,
  pageNumber,
  sorting,
}: HeapRecordingListOptionsProps) {
  return queryOptions({
    queryKey: heapRecordingKeys.list({
      jobsiteId,
      filter,
      pageSize,
      pageNumber,
      sorting,
    }),
    queryFn: async () => {
      const params: HeapRecordingControllerGetRecordingsData = {
        path: { jobsiteId: jobsiteId },
        query: {
          filter: [filter],
          limit: pageSize,
          offset: pageNumber * pageSize,
          ...sorting,
          sortField: sortFieldDto[sorting.sortField] ?? sorting.sortField,
        },
      };

      const response = await HeapRecordingService.heapRecordingControllerGetRecordings(params);

      if (response.data !== undefined) {
        return { ...response.data, data: response.data.data.map((batch) => toHeapRecordingBatch(batch)) };
      } else {
        throw (response as any).error || new Error(response.response?.statusText || 'Unknown error');
      }
    },
    enabled: !!jobsiteId,
  });
}

export function useHeapRecordingList(props: HeapRecordingListOptionsProps) {
  return useQuery(heapRecordingListOptions(props));
}

function toHeapRecordingBatch(batch: HeapRecordingDTO[]): HeapRecordingBatch {
  return {
    id: batch[0].batch_id ?? batch[0].id,
    recordedAt: new Date(batch[0].recorded_at),
    licensePlate: batch[0].license_plate,
    hasDetections: batch.some((cam) => cam.has_detections),
    archived: batch.some((cam) => cam.archived),
    jobsiteId: batch[0].jobsite_id,
    version: batch[0].version,
    cameras: batch.map((cam) => toHeapRecording(cam)),
  };
}

function toHeapRecording(recording: HeapRecordingDTO): HeapRecording {
  return {
    id: recording.id,
    recordedAt: new Date(recording.recorded_at),
    licensePlate: recording.license_plate,
    hasDetections: recording.has_detections,
    detections: transformDetectionsDict(recording.detections_dict),
    version: recording.version,
    jobsiteId: recording.jobsite_id,
    archived: recording.archived,
    extraFields: recording.extra_fields,
    cameraId: recording.camera_id,
    masks: recording.masks,
  };
}

/**
 * Transform detections_dict from API format to component format
 * API format:  { classes: [["a"], ["b"]], scores: [[0.9], [0.8]], bbox: [[x,y,x,y], [...]], sources: [["stable"], ["alpha"]] }
 * Component:   [{ classes: ["a"], scores: [0.9], bbox: [x,y,x,y], sources: ["stable"] }, ...]
 */
function transformDetectionsDict(detectionsDict: DetectionsDictDTO | undefined): HeapDetection[] {
  if (!detectionsDict || !detectionsDict.classes) {
    return [];
  }

  const { classes, scores, bbox } = detectionsDict;
  const sources = detectionsDict?.sources;
  const length = classes.length;
  const detections: HeapDetection[] = [];

  for (let i = 0; i < length; i++) {
    detections.push({
      classes: classes[i],
      scores: scores[i],
      bbox: bbox[i],
      sources: sources?.[i],
    });
  }

  return detections;
}

const sortFieldDto: Partial<Record<keyof HeapRecordingBatch, string>> = {
  recordedAt: 'recorded_at',
  licensePlate: 'license_plate',
  hasDetections: 'has_detections',
  version: 'version',
  archived: 'archived',
};
